import { createTheme, darken, alpha, lighten } from '@mui/material/styles';

import { getSpacing, multiplyBy } from '../../styles/styleUtils';

// Tweak this to change every ToggleButtonGroup corner radius app-wide.
const TOGGLE_BORDER_RADIUS = 8;

/**
 * Design tokens reach the theme as `var()` strings — MUI passes styleOverrides
 * values to emotion verbatim, so a string stays a string and the browser
 * resolves it live. Each carries today's value as a fallback, so the theme is
 * still correct if the token sink is missing (tests, SSR, a failed stylesheet).
 *
 * Values that JavaScript reads — shape.borderRadius (MUI does arithmetic on
 * it), spacing, and every palette colour fed to alpha()/darken()/lighten() —
 * deliberately stay real numbers/hex. A var() there yields NaN or a malformed
 * colour that emotion drops silently.
 */
const RADIUS_SM = 'var(--radius-sm, 4px)';
const RADIUS_MD = 'var(--radius-md, 8px)';
const RADIUS_LG = 'var(--radius-lg, 12px)';
const FONT_XS = 'var(--font-xs, 10px)';
const FONT_MD = 'var(--font-md, 14px)';
const FONT_TITLE = 'var(--font-title, 18px)';

// ── Light palette ───────────────────────────────────────────────────────────────
const palette = {
    mode: 'light',
    primary: {
        main: '#4A6FA5',
        dark: '#364f75',
        light: '#d0dfe4',
        darken10: darken('#4A6FA5', 0.1),
        lighten90: lighten('#4A6FA5', 0.9),
        lighten95: lighten('#4A6FA5', 0.95),
        lighten80: lighten('#4A6FA5', 0.8)
    },
    grey: {
        fade50: alpha('#808080', 0.5),
        910: '#f8f9ff',
        920: '#f4f5fd',
        930: '#eeeff8',
        940: '#e6e7f1',
        950: '#dfe0ea',
        960: '#d1d2db',
        970: '#a6a6b9',
        980: '#7a7b97',
        990: '#3b3e66',
        1000: '#070919'
    },
    secondary: {
        main: '#3B5998',
        lighten50: lighten('#3B5998', 0.5),
        lighten90: lighten('#3B5998', 0.9),
        darken25: darken('#3B5998', 0.25),
        darken50: darken('#3B5998', 0.5)
    },
    // The top bar and the dialog title bars only — Pegasus navy, from the
    // pegasusisrael.co.il header. Its own key rather than `secondary`, which
    // also colours buttons, icons and sidebar text across the app.
    brandHeader: '#1B2F5B',
    // The bottom bar and its text (Appearance → Footer). Paired, so a light
    // footer can carry dark text.
    brandFooter: '#1B2F5B',
    brandFooterText: 'rgba(255, 255, 255, 0.8)',
    accent: {
        main: '#ffb01e',
        light: lighten('#ffb01e', 0.2),
        lighten50: lighten('#ffb01e', 0.5),
        dark: darken('#ffb01e', 0.1)
    },
    error: {
        main: '#f83245',
        lighten90: lighten('#f83245', 0.9),
        darken70: darken('#f83245', 0.7)
    },
    success: {
        main: '#1bc943',
        lighten90: lighten('#1bc943', 0.9),
        darken70: darken('#1bc943', 0.7)
    },
    warning: {
        main: '#f4772e',
        lighten90: lighten('#f4772e', 0.9),
        lighten80: lighten('#f4772e', 0.8),
        darken70: darken('#f4772e', 0.7)
    },
    info: {
        main: '#2196f3',
        lighten90: lighten('#2196f3', 0.9),
        darken70: darken('#2196f3', 0.7)
    },
    helpers: {
        primary: '#263055',
        main: 'rgba(25, 46, 91, .035)'
    },
    common: {
        white50: alpha('#fff', 0.5),
        black50: alpha('#000', 0.5)
    },
    background: {
        default: '#f4f5fd',
        paper: '#ffffff'
    },
    text: {
        primary: '#2c2c2c',
        secondary: '#6b7280'
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    contrastThreshold: 3,
    tonalOffset: 0.1
};

const themeVars = {
    spacer: '0.4rem',
    layoutSpacer: '0.8rem',
    appSidebarTransition: 'cubic-bezier(0.685, 0.0473, 0.346, 1)',
    headerHeight: '62px',
    // Owned by --footer-height in base.scss so the footer band, the sidebar's
    // matching band and --content-height all move together.
    footerHeight: 'var(--footer-height, 48px)',
    contentHeight: 'var(--content-height, calc(var(--app-vh, 100vh) - 270px))',
    // Duplicate of SIDEBAR_WIDTH in layoutConstants.ts — kept in step by
    // hand until the remaining consumers move off themeVars.
    sidebarWidth: '230px',
    sidebarWidthCollapsed: '90px',
    sidebarFooterHeight: '64px',
    sidebarFavoritesHeight: '150px',
    fontSizeXxl: '28px',
    fontSizeXl: '24px',
    fontSizeLg: '20px',
    fontSizeBase: '16px',
    fontSizeSm: '14px',
    fontSizeXs: '12px',
    transitionBase: 'all .2s ease-in-out',
    borderRadius: '.65rem',
    borderRadiusLg: '.75rem',
    borderRadiusSm: '.29rem',
    borderRadiusXs: '.2rem',
    popoverMaxWidth: '276px',
    navItemIconSize: '28px'
};

const globals = {
    roundedXs: {
        borderRadius: themeVars.borderRadiusXs
    },
    roundedSm: {
        borderRadius: themeVars.borderRadiusSm
    },
    roundedLg: {
        borderRadius: themeVars.borderRadiusLg
    },
    textNowrap: {
        whiteSpace: 'nowrap'
    },
    dBlock: {
        display: 'block'
    },
    dFlex: {
        display: 'flex'
    },
    flexRow: {
        display: 'flex',
        flexDirection: 'row'
    },
    flexColumn: {
        display: 'flex',
        flexDirection: 'column'
    },
    alignItemsCenter: {
        alignItems: 'center'
    },
    textCenter: {
        textAlign: 'center'
    },
    textLeft: {
        textAlign: 'left'
    },
    textRight: {
        textAlign: 'right'
    },
    fontSizeXxl: {
        fontSize: themeVars.fontSizeXxl
    },
    fontSizeXl: {
        fontSize: themeVars.fontSizeXl
    },
    fontSizeLg: {
        fontSize: themeVars.fontSizeLg
    },
    fontSizeBase: {
        fontSize: themeVars.fontSizeBase
    },
    fontSizeSm: {
        fontSize: themeVars.fontSizeSm
    },
    fontSizeXs: {
        fontSize: themeVars.fontSizeXs
    },
    popoverCustomLg: {
        maxWidth: multiplyBy(themeVars.popoverMaxWidth, 1.5)
    },
    popoverCustomMd: {
        width: multiplyBy(themeVars.popoverMaxWidth, 1.5),
        maxWidth: multiplyBy(themeVars.popoverMaxWidth, 1.5)
    },
    popoverCustomXl: {
        width: multiplyBy(themeVars.popoverMaxWidth, 2),
        maxWidth: multiplyBy(themeVars.popoverMaxWidth, 2)
    },
    popoverCustomXxl: {
        width: multiplyBy(themeVars.popoverMaxWidth, 3),
        maxWidth: multiplyBy(themeVars.popoverMaxWidth, 3)
    },
    textCapitalize: {
        textTransform: 'capitalize'
    },
    fontWeightBold: {
        fontWeight: 'bold'
    },
    opacity0: {
        opacity: '0'
    },
    opacity1: {
        opacity: '0.2'
    },
    opacity2: {
        opacity: '0.4'
    },
    opacity3: {
        opacity: '0.6'
    },
    opacity4: {
        opacity: '0.8'
    },
    opacity5: {
        opacity: '1'
    },
    navLinkIcon: {
        width: themeVars.navItemIconSize,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '5px',
        fontSize: themeVars.fontSizeLg
    },
    getPadding: (paddingValue, directions) => getSpacing(paddingValue, directions, 'padding'),
    getMargin: (marginValue, directions) => getSpacing(marginValue, directions, 'margin'),
    overflowHidden: {
        overflow: 'hidden'
    },
    bgTransparent: {
        backgroundColor: 'transparent'
    },
    lineHeight1: {
        lineHeight: '1'
    },
    lineHeightSm: {
        lineHeight: '1.3'
    },
    lineHeightMd: {
        lineHeight: '2'
    },
    width100: {
        width: '100%'
    },
    // d-50 === ...globals.d50
    ...[30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140].reduce((acc, num) => {
        acc[`d${num}`] = {
            height: `${num}px`,
            lineHeight: `${num}px`,
            width: `${num}px`
        };
        return acc;
    }, {}),
    bgMidnightBloom: {
        backgroundImage: 'linear-gradient(-20deg, #2b5876 0%, #4e4376 100%)'
    },
    borderRadiusLg: {
        borderRadius: '.75rem'
    },
    borderRadiusSm: {
        borderRadius: '.29rem'
    },
    borderRadiusXs: {
        borderRadius: '.2rem'
    }
};

// ── Dark palette ────────────────────────────────────────────────────────────────
const darkPalette = {
    mode: 'dark',
    primary: {
        main: '#6B93D6',
        dark: '#4A6FA5',
        light: '#2a3a52',
        darken10: darken('#6B93D6', 0.1),
        lighten90: '#1e2a3a',
        lighten95: '#171e2c',
        lighten80: '#233248'
    },
    grey: {
        ...palette.grey,
        50: '#1e1e2e',
        100: '#252538',
        200: '#2d2d42',
        300: '#3a3a52',
        400: '#5a5a7a',
        500: '#7a7a9a',
        600: '#9a9ab0',
        700: '#b0b0c4',
        800: '#c8c8d8',
        900: '#e0e0ea',
        910: '#1a1a2e',
        920: '#1e1e32',
        930: '#222238',
        940: '#282840',
        950: '#2e2e48',
        960: '#3a3a52',
        970: '#6a6a82',
        980: '#9a9ab0',
        990: '#c8c8d8',
        1000: '#f0f0f8'
    },
    secondary: {
        main: '#5B7DC8',
        lighten50: '#2a3650',
        lighten90: '#1c2438',
        darken25: darken('#5B7DC8', 0.25),
        darken50: darken('#5B7DC8', 0.5)
    },
    // Dark mode frames the app in its own surface colour (the sidebar's
    // background.paper) rather than a brand blue: navy — and before it the
    // lifted #5B7DC8 — was one more dark shade competing with the page.
    brandHeader: '#1a1a2e',
    brandFooter: '#1a1a2e',
    brandFooterText: 'rgba(255, 255, 255, 0.8)',
    accent: palette.accent,
    error: palette.error,
    success: palette.success,
    warning: palette.warning,
    info: palette.info,
    helpers: {
        primary: '#c8d0e0',
        main: 'rgba(200, 210, 230, .08)'
    },
    common: {
        white50: alpha('#fff', 0.5),
        black50: alpha('#000', 0.5)
    },
    background: {
        default: '#121220',
        paper: '#1a1a2e'
    },
    text: {
        primary: '#e0e0ea',
        secondary: '#9a9ab0',
        disabled: '#5a5a7a'
    },
    divider: '#2d2d42',
    contrastThreshold: 3,
    tonalOffset: 0.1
};

// ── Build theme for a given palette ──────────────────────────────────────────
function createAppTheme(p) {
    const isDark = p.mode === 'dark';
    return {
        spacing: 6,
        palette: p,
        shape: {
            borderRadius: 8
        },
        components: {
            // Shared banner/notice look — every <Alert> (amend-voucher notice, auto-rate notice, etc.)
            // renders consistently. Severity colors still come from the palette; this only unifies
            // shape, alignment, and spacing across light and dark.
            MuiAlert: {
                defaultProps: {
                    variant: 'standard'
                },
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        alignItems: 'center',
                        fontSize: '0.85rem',
                        padding: '2px 12px'
                    },
                    message: {
                        padding: '6px 0'
                    }
                }
            },
            MuiButton: {
                styleOverrides: {
                    text: {
                        paddingLeft: '14px',
                        paddingRight: '14px'
                    },
                    containedSizeSmall: {
                        paddingLeft: '14px',
                        paddingRight: '14px'
                    },
                    containedSizeLarge: {
                        padding: '4px 10px',
                        fontSize: '0.8rem'
                    },
                    root: {
                        textTransform: 'none',
                        fontWeight: 'normal',
                        margin: '0 2px',
                        // 1.4 with 5px padding lands a default button on the
                        // control height token; 1.25 was tuned for the old
                        // content-sized button.
                        lineHeight: 1.4,
                        padding: '5px 12px',
                        minHeight: 'var(--control-h-md, 32px)',
                        borderRadius: RADIUS_SM
                    },
                    outlined: {
                        padding: '6px 12px'
                    },
                    // System-wide soft pill style for outlined primary buttons
                    // (Back to List, Add Passenger, Edit, Receipt, Repay,
                    // View Voucher, etc.). Tokens chosen to read as
                    // "secondary action": clearly clickable but visually
                    // recessed compared to the contained-primary CTAs.
                    outlinedPrimary: {
                        color: isDark ? p.primary.main : '#4568a8',
                        borderColor: isDark ? 'var(--input-border)' : '#9fb2d1',
                        backgroundColor: isDark ? 'transparent' : '#ffffff',
                        fontWeight: 500,
                        '&:hover': {
                            borderColor: isDark ? p.primary.main : '#4568a8',
                            backgroundColor: isDark ? alpha(p.primary.main, 0.1) : '#f5f8ff'
                        },
                        '&.Mui-disabled': {
                            color: isDark ? alpha(p.primary.main, 0.4) : alpha('#4568a8', 0.4),
                            borderColor: isDark ? 'rgba(107,147,214,0.25)' : alpha('#9fb2d1', 0.5),
                            backgroundColor: isDark ? 'transparent' : '#ffffff'
                        }
                    },
                    // Disabled contained buttons: keep clearly disabled but with
                    // enough contrast to stay readable in dark mode (default MUI
                    // disabled is nearly invisible on a dark surface).
                    contained: {
                        '&.Mui-disabled': isDark
                            ? {
                                  backgroundColor: 'rgba(107,147,214,0.18)',
                                  color: 'rgba(224,224,234,0.55)'
                              }
                            : undefined
                    }
                }
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        padding: '4px',
                        margin: '0 4px'
                    }
                }
            },
            MuiToggleButtonGroup: {
                styleOverrides: {
                    root: {
                        borderRadius: RADIUS_MD
                    },
                    grouped: {
                        '&:not(:first-of-type)': {
                            borderLeft: isDark
                                ? '1px solid var(--input-border)'
                                : '1px solid rgba(0, 0, 0, 0.12)'
                        },
                        '&:first-of-type': {
                            borderTopLeftRadius: TOGGLE_BORDER_RADIUS,
                            borderBottomLeftRadius: TOGGLE_BORDER_RADIUS
                        },
                        '&:last-of-type': {
                            borderTopRightRadius: TOGGLE_BORDER_RADIUS,
                            borderBottomRightRadius: TOGGLE_BORDER_RADIUS
                        }
                    }
                }
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        padding: '2px 12px',
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        fontWeight: 500,
                        backgroundColor: isDark ? 'var(--input-bg)' : '#fff',
                        color: isDark ? p.text.secondary : undefined,
                        borderColor: isDark ? 'var(--input-border)' : undefined,
                        '&:hover': {
                            backgroundColor: isDark
                                ? alpha(p.primary.main, 0.12)
                                : alpha(p.primary.main, 0.04)
                        },
                        '&.Mui-selected': {
                            backgroundColor: p.primary.main,
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: p.primary.dark
                            }
                        }
                    }
                }
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: `${RADIUS_LG} !important`,
                        boxShadow: isDark
                            ? '0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)'
                            : '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
                        backgroundColor: isDark ? p.background.paper : undefined
                    },
                    paperFullWidth: {
                        maxWidth: '1000px',
                        maxHeight: '90vh'
                    }
                },
                defaultProps: {
                    BackdropProps: {
                        style: {
                            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(9, 30, 66, 0.4)'
                        }
                    }
                }
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        padding: '20px 24px 12px',
                        '&.MuiDialogContent-root': {
                            paddingTop: '20px'
                        }
                    }
                }
            },
            MuiPopover: {
                styleOverrides: {
                    paper: {
                        position: 'absolute !important'
                    }
                }
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        position: 'absolute !important'
                    }
                }
            },
            MuiPaper: {
                styleOverrides: {
                    rounded: {
                        borderRadius: RADIUS_LG
                    }
                }
            },
            MuiInputAdornment: {
                styleOverrides: {
                    positionEnd: {
                        marginLeft: '4px'
                    },
                    positionStart: {
                        marginRight: '4px'
                    }
                }
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: p.primary.main,
                        padding: '8px 8px',
                        fontSize: '12px',
                        opacity: `0.85 !important`,
                        lineHight: 1
                    },
                    arrow: {
                        color: p.primary.main
                    }
                }
            },
            MuiToolbar: {
                styleOverrides: {
                    regular: {
                        minHeight: '20px !important',
                        padding: '5px 0'
                    }
                }
            },
            MuiTable: {
                styleOverrides: {
                    root: {
                        width: '100%'
                    }
                }
            },
            MuiAccordionSummary: {
                styleOverrides: {
                    root: {
                        backgroundColor: p.grey[950],
                        borderRadius: '5px 0 5px 0',
                        boxShadow: 'none',
                        '&.Mui-expanded': {
                            minHeight: '48px'
                        }
                    },
                    content: {
                        '&.Mui-expanded': {
                            margin: 0
                        }
                    }
                }
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        '&:before': {
                            backgroundColor: 'transparent'
                        }
                    }
                }
            },
            MuiTableBody: {
                styleOverrides: {
                    root: {
                        '&:nth-of-type(2n)': {
                            backgroundColor: isDark ? p.grey[50] : '#fafafa'
                        }
                    }
                }
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&:hover': {
                            cursor: 'pointer',
                            backgroundColor: p.primary.lighten80
                        }
                    },
                    footer: {
                        '&:hover': {
                            backgroundColor: 'transparent',
                            cursor: 'auto'
                        }
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        padding: '4px 8px'
                    }
                }
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        backgroundColor: isDark ? 'var(--input-bg)' : p.background.paper,
                        color: isDark ? 'var(--input-text)' : p.text.primary
                    },
                    input: {
                        padding: '3px 0',
                        // The colour must be set in both themes, not just dark. The
                        // opacity:1 below applies either way, so in light mode a
                        // placeholder fell back to the input's own text colour at full
                        // strength — MUI's default 0.42 was the only thing greying it —
                        // and hints read as real, typed-in values.
                        '&::placeholder': {
                            color: 'var(--input-placeholder)',
                            opacity: 1
                        }
                    }
                }
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        width: '0.7em'
                    },
                    fontSizeSmall: {
                        width: '1.2em'
                    }
                }
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        padding: '5px',
                        color: isDark ? 'var(--control-muted)' : undefined
                    }
                }
            },
            MuiRadio: {
                styleOverrides: {
                    root: {
                        color: isDark ? 'var(--control-muted)' : undefined
                    }
                }
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        marginTop: '10px'
                    }
                }
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        fontSize: FONT_XS,
                        lineHeight: '12px',
                        marginTop: 0,
                        marginBottom: '4px'
                    }
                }
            },
            MuiFormLabel: {
                styleOverrides: {
                    asterisk: {
                        color: p.error.main,
                        '&.Mui-error': {
                            color: p.error.main
                        }
                    }
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        // Brighter field labels in dark mode — the default
                        // text.secondary reads too faint on the dark forms.
                        color: isDark ? 'var(--input-label)' : undefined,
                        '&.Mui-focused': {
                            color: isDark ? 'var(--focus-ring)' : undefined
                        },
                        '&.Mui-disabled': {
                            color: isDark ? 'var(--input-text-disabled)' : undefined
                        }
                    }
                }
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        boxShadow: 'none',
                        borderRadius: 0
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: RADIUS_MD,
                        backgroundColor: isDark ? 'var(--input-bg)' : p.background.paper,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDark ? 'var(--input-border)' : undefined
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDark ? 'var(--input-border-hover)' : undefined
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDark ? 'var(--focus-ring)' : undefined,
                            borderWidth: isDark ? 1 : undefined
                        },
                        '&.Mui-disabled': {
                            backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : undefined,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: isDark ? 'rgba(61,68,104,0.5)' : undefined
                            }
                        }
                    },
                    input: {
                        padding: '6px 10px',
                        fontSize: FONT_MD,
                        color: isDark ? 'var(--input-text)' : undefined,
                        '&::placeholder': {
                            color: 'var(--input-placeholder)',
                            opacity: 1
                        },
                        // Disabled value text stays legible (clearly muted, not
                        // near-invisible) in dark mode.
                        '&.Mui-disabled': {
                            WebkitTextFillColor: isDark ? 'rgba(224,224,234,0.62)' : undefined,
                            color: isDark ? 'rgba(224,224,234,0.62)' : undefined
                        },
                        '&:-webkit-autofill': {
                            WebkitBoxShadow: isDark
                                ? '0 0 0 100px var(--input-bg) inset'
                                : `0 0 0 100px ${p.background.paper} inset`,
                            WebkitTextFillColor: isDark ? 'var(--input-text)' : p.text.primary
                        }
                    },
                    adornedStart: {
                        paddingLeft: '8px'
                    },
                    adornedEnd: {
                        paddingRight: '6px'
                    }
                }
            },
            MuiSelect: {
                styleOverrides: {
                    select: {
                        backgroundColor: isDark ? 'var(--input-bg)' : p.background.paper,
                        color: isDark ? 'var(--input-text)' : undefined
                    },
                    icon: {
                        color: 'var(--input-placeholder)'
                    }
                }
            },
            MuiAutocomplete: {
                styleOverrides: {
                    paper: {
                        backgroundColor: p.background.paper
                    },
                    inputRoot: {
                        backgroundColor: isDark ? 'var(--input-bg)' : p.background.paper,
                        color: isDark ? 'var(--input-text)' : undefined
                    },
                    tag: {
                        color: isDark ? 'var(--input-text)' : undefined
                    },
                    clearIndicator: {
                        color: 'var(--input-placeholder)'
                    },
                    popupIndicator: {
                        color: 'var(--input-placeholder)'
                    },
                    option: {
                        '&[aria-selected="true"]': {
                            backgroundColor: isDark ? 'rgba(107,147,214,0.16)' : undefined
                        },
                        '&.Mui-focused': {
                            backgroundColor: isDark ? 'rgba(107,147,214,0.12)' : undefined
                        }
                    }
                }
            }
        },
        typography: {
            fontFamily: `"Helvetica Neue", "Roboto", "Helvetica", "Arial", sans-serif`,
            h1: {
                fontWeight: 600,
                fontSize: FONT_TITLE,
                // A real line box. This used to be `lineHeight: 0` + 6px of
                // padding, which left the element 6px tall while its text was
                // 18px — the glyphs overflowed their own box and whatever
                // followed the title was painted over their lower half.
                lineHeight: 1.35,
                marginBottom: '0px'
            },
            h2: {
                fontWeight: 500,
                fontSize: '26px',
                lineHeight: '32px'
            },
            h3: {
                fontWeight: 500,
                fontSize: '24px',
                lineHeight: '28px'
            },
            h4: {
                fontWeight: 500,
                fontSize: '20px',
                lineHeight: '24px'
            },
            h5: {
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '22px'
            },
            h6: {
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px'
            },
            subtitle1: {
                fontSize: '16px',
                lineHeight: '25px'
            },
            subtitle2: {
                fontWeight: 400,
                fontSize: '14px'
            },
            body1: {
                fontSize: '14px'
            },
            body2: {
                fontSize: '12px'
            },
            button: {
                fontSize: FONT_MD
            },
            caption: {
                fontSize: '12px'
            },
            overline: {
                fontSize: '12px',
                fontWeight: 500,
                textTransform: 'uppercase'
            }
        },
        themeVars,
        globals
    };
}

// Default export = light theme (backwards compatible)
const MuiTheme = createTheme(createAppTheme(palette));

/**
 * Re-derives the brand shades of a palette from a tenant's two colours.
 *
 * Every `lighten90` / `darken10` in the palettes above is computed from the
 * brand hex, so a tenant that only overrode `main` would end up with hover and
 * tint states still keyed to Taytan blue — the app would look half-rebranded.
 * The dark palette lifts its primary rather than using the raw colour, the same
 * relationship the shipped dark palette has to the light one.
 */
const applyBrandColors = (pal, color) => {
    if (!color) return pal;
    const isDark = pal.mode === 'dark';
    const primary = isDark ? lighten(color.primary, 0.25) : color.primary;

    return {
        ...pal,
        primary: {
            ...pal.primary,
            main: primary,
            dark: darken(primary, 0.2),
            light: isDark ? darken(primary, 0.55) : lighten(primary, 0.7),
            darken10: darken(primary, 0.1),
            lighten90: isDark ? darken(primary, 0.75) : lighten(primary, 0.9),
            lighten95: isDark ? darken(primary, 0.82) : lighten(primary, 0.95),
            lighten80: isDark ? darken(primary, 0.68) : lighten(primary, 0.8)
        },
        accent: {
            ...pal.accent,
            main: color.accent,
            light: lighten(color.accent, 0.2),
            lighten50: lighten(color.accent, 0.5),
            dark: darken(color.accent, 0.1)
        },
        // Secondary and Header apply to the light theme only: the dark palette
        // keeps its own lifted blues for both (a navy top bar or a #3B5998
        // button is unreadable on a dark page), so a tenant's pick there
        // would need its own dark counterpart.
        ...(!isDark && color.secondary
            ? {
                  secondary: {
                      ...pal.secondary,
                      main: color.secondary,
                      lighten50: lighten(color.secondary, 0.5),
                      lighten90: lighten(color.secondary, 0.9),
                      darken25: darken(color.secondary, 0.25),
                      darken50: darken(color.secondary, 0.5)
                  }
              }
            : {}),
        ...(!isDark && color.header ? { brandHeader: color.header } : {}),
        ...(!isDark && color.footer ? { brandFooter: color.footer } : {}),
        ...(!isDark && color.footerText ? { brandFooterText: color.footerText } : {})
    };
};

export { createAppTheme, palette, darkPalette, applyBrandColors };
export default MuiTheme;
